import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ChevronDown, Search } from 'lucide-react';
import { glossary, buildGlossaryRegex, lookupGlossaryTerm, type GlossaryEntry } from '@/data/glossary';
import { PlayDiagram } from '@/components/PlayDiagram';

interface GlossaryPanelProps {
  /** Lesson sport — used to filter the term bank. */
  sport: 'football' | 'soccer' | 'basketball' | 'baseball' | string;
  /** Optional plain text from the current lesson — only terms appearing here are shown. */
  contextText?: string;
  className?: string;
}

const SPORT_LABEL: Record<string, string> = {
  football: 'Football',
  soccer: 'Soccer',
  basketball: 'Basketball',
  baseball: 'Baseball',
};

export function GlossaryPanel({ sport, contextText, className }: GlossaryPanelProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const sportEntries = useMemo<GlossaryEntry[]>(() => {
    return glossary
      .filter(e => e.sport === sport || e.sport === 'all')
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [sport]);

  // If the lesson provides context text, restrict to the terms actually
  // highlighted in it — using the same matcher as the in-text glossary marks.
  const contextEntries = useMemo<GlossaryEntry[]>(() => {
    if (!contextText) return sportEntries;
    const regex = buildGlossaryRegex();
    if (!regex) return [];
    const found = new Map<string, GlossaryEntry>();
    for (const m of contextText.matchAll(regex)) {
      const entry = lookupGlossaryTerm(m[0]);
      if (entry && !found.has(entry.term)) found.set(entry.term, entry);
    }
    return [...found.values()].sort((a, b) => a.term.localeCompare(b.term));
  }, [sportEntries, contextText]);

  // The button count is the number of DISTINCT glossary terms marked in the
  // lesson — the same terms the panel lists — not the number of occurrences.
  const markedCount = contextEntries.length;

  const filtered = useMemo(() => {
    if (!query.trim()) return contextEntries;
    const q = query.trim().toLowerCase();
    return contextEntries.filter(
      e => e.term.toLowerCase().includes(q) || e.definition.toLowerCase().includes(q),
    );
  }, [contextEntries, query]);

  if (sportEntries.length === 0) return null;
  // Never show "(0 in this lesson)" — hide when the lesson has no terms.
  if (contextText !== undefined && contextEntries.length === 0) return null;

  return (
    <div className={`rounded-xl border-2 border-border bg-card overflow-hidden ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="font-display font-bold text-sm">
            {SPORT_LABEL[sport] ?? 'Sport'} Glossary
          </span>
          <span className="text-xs font-body text-muted-foreground">
            ({markedCount} {contextText ? 'in this lesson' : 'terms'})
          </span>
        </div>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="panel-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search terms…"
                  className="w-full pl-8 pr-3 py-2 text-sm font-body rounded-lg bg-muted/40 border border-border focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {filtered.length === 0 ? (
                <p className="text-xs font-body text-muted-foreground py-2 text-center">
                  No matching terms.
                </p>
              ) : (
                <ul className="divide-y divide-border max-h-64 overflow-y-auto -mx-1">
                  {filtered.map(entry => {
                    const isOpen = expanded === entry.term;
                    return (
                      <li key={entry.term}>
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : entry.term)}
                          className="w-full text-left py-2 px-1 hover:bg-muted/30 rounded transition-colors"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-display font-bold text-sm capitalize text-foreground">
                              {entry.term}
                            </span>
                            <ChevronDown
                              className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                          {isOpen && (
                            <div className="mt-2 space-y-2">
                              <p className="font-body text-xs leading-relaxed text-foreground/80">
                                {entry.definition}
                              </p>
                              {entry.diagram && (
                                <div className="pt-1">
                                  <PlayDiagram diagram={entry.diagram} />
                                </div>
                              )}
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
